import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';
import useScrollToTop from '@/hooks/useScrollToTop';

export default function ComicDetailPage() {
  useScrollToTop();
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '1', 10);

  const comic = useMemo(() => comics.find(c => c.id === comicId), [comicId]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter(c => c.category === comic.category && c.id !== comic.id)
      .slice(0, 4);
  }, [comic]);

  const chapterList = useMemo(() => {
    if (!comic) return [];
    const total = comic.chapters;
    return Array.from({ length: Math.min(total, 20) }, (_, i) => ({
      number: total - i,
      title: `第${total - i}话`,
      date: new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    }));
  }, [comic]);

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 韩漫之家" description="抱歉，您查找的漫画不存在。" />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">抱歉，您查找的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = categories.find(c => c.slug === comic.category)?.name || comic.category;
  const formatViews = (v: number) => {
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
    return String(v);
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫之家免费在线观看`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-01',
    dateModified: comic.updatedAt,
    url: `https://www.manhuazhijia.com/comic/${comic.id}`,
  };

  return (
    <>
      <SEOHead
        title={`${comic.title}_${comic.author}_免费韩漫在线观看 - 韩漫之家`}
        description={`${comic.title} - ${comic.description}。在韩漫之家免费在线观看${comic.title}，下拉式流畅阅读，最新章节即时更新。`}
        keywords={`${comic.title},${comic.author},免费韩漫,韩漫在线观看,下拉式韩漫,${catName}韩漫`}
        canonicalUrl={`https://www.manhuazhijia.com/comic/${comic.id}`}
        ogType="article"
        schemaData={[articleSchema]}
      />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: `${catName}韩漫`, href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-full sm:w-[260px] mx-auto md:mx-0">
                <div className="overflow-hidden rounded-xl bg-background-100" style={{ aspectRatio: '2/3' }}>
                  <img
                    src={comic.coverImageLarge}
                    alt={comic.title}
                    title={`${comic.title} - 免费韩漫在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-foreground-500 text-sm mb-4">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '已完结' ? 'bg-background-200 text-foreground-600' : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">评分</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-eye-line text-foreground-400"></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900">{formatViews(comic.views)}</span>
                    <span className="text-xs text-foreground-400">阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-file-list-line text-foreground-400"></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900">{comic.chapters}</span>
                    <span className="text-xs text-foreground-400">话</span>
                  </div>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6 bg-background-100 rounded-xl p-5">
                  {comic.description}
                </p>

                <div className="flex gap-3">
                  <Link
                    to={`/comic/${comic.id}/read/1`}
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                  </Link>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-400 hover:text-primary-500 hover:border-primary-200 transition-colors cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-400 hover:text-primary-500 hover:border-primary-200 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line"></i>
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">章节列表</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {chapterList.map((ch) => (
                  <Link
                    key={ch.number}
                    to={`/comic/${comic.id}/read/${ch.number}`}
                    className="flex items-center justify-between px-4 py-3 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-700 font-medium">{ch.title}</span>
                    <span className="text-xs text-foreground-400">{ch.date}</span>
                  </Link>
                ))}
              </div>
              {comic.chapters > 20 && (
                <div className="text-center mt-4">
                  <button className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                    查看全部 {comic.chapters} 话 →
                  </button>
                </div>
              )}
            </section>

            {relatedComics.length > 0 && (
              <section className="mt-14">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5" data-product-shop>
                  {relatedComics.map((rc) => (
                    <ComicCard key={rc.id} {...rc} coverImage={rc.coverImage} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}